/**
 * clinvarDatabase-API.js - FIXED with proper session-based caching
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://geneie513-api-449519797657.us-central1.run.app';

// Session-based cache structure - each session gets its own isolated cache
const sessionCaches = new Map(); // Map<sessionId, { cache: Map, timestamp: number }>
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Generate unique session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create cache for a session
 */
const getSessionCache = (sessionId) => {
  if (!sessionCaches.has(sessionId)) {
    console.log(`🆕 Creating new cache for session: ${sessionId}`);
    sessionCaches.set(sessionId, {
      cache: new Map(),
      timestamp: Date.now()
    });
  }
  return sessionCaches.get(sessionId);
};

/**
 * Clear cache for specific session
 */
export const clearCache = (sessionId) => {
  if (sessionId && sessionCaches.has(sessionId)) {
    const sessionData = sessionCaches.get(sessionId);
    console.log(`✅ Cache cleared for session ${sessionId} (had ${sessionData.cache.size} items)`);
    sessionCaches.delete(sessionId);
  } else if (sessionId) {
    console.log(`ℹ️ No cache to clear for session ${sessionId}`);
  }
};

/**
 * Clean up old sessions (prevent memory leaks)
 */
const cleanupOldSessions = () => {
  const now = Date.now();
  let cleaned = 0;
  
  for (const [sessionId, sessionData] of sessionCaches.entries()) {
    if (now - sessionData.timestamp > CACHE_DURATION) {
      sessionCaches.delete(sessionId);
      cleaned++;
    }
  }
  
  if (cleaned > 0) {
    console.log(`🗑️ Cleaned up ${cleaned} expired sessions`);
  }
};

// Run cleanup every 5 minutes
setInterval(cleanupOldSessions, 5 * 60 * 1000);

/**
 * Normalize rsID to match server format (lowercase, with 'rs' prefix)
 */
const normalizeRsid = (rsid) => {
  if (!rsid) return null;
  const normalized = rsid.toLowerCase().trim();
  return normalized.startsWith('rs') ? normalized : `rs${normalized}`;
};

/**
 * Check if cache is still valid for a session
 */
const isCacheValid = (sessionId) => {
  if (!sessionCaches.has(sessionId)) return true; // No cache yet is "valid"
  
  const sessionData = sessionCaches.get(sessionId);
  const now = Date.now();
  const isValid = (now - sessionData.timestamp) < CACHE_DURATION;
  
  if (!isValid) {
    console.log(`⚠️ Cache expired for session ${sessionId}, clearing...`);
    clearCache(sessionId);
  }
  
  return isValid;
};

export const loadClinVarData = async () => {
  console.log('ClinVar database: Using backend API for queries');
  
  try {
    const response = await fetch(`${API_URL}/health`);
    const health = await response.json();
    console.log(`✅ Backend connected`);
    return true;
  } catch (err) {
    console.warn('⚠️ Backend not available, using fallback data');
    return false;
  }
};

/**
 * Fetch multiple SNPs with automatic chunking and progressive loading
 * Returns results as they arrive for immediate display
 */
export const fetchClinVarBatch = async (rsids, onProgress = null, sessionId = null) => {
  // Generate session ID if not provided
  if (!sessionId) {
    sessionId = generateSessionId();
    console.log(`🆕 Auto-generated session ID: ${sessionId}`);
  }
  
  console.log(`📊 [${sessionId}] Fetching ${rsids.length.toLocaleString()} SNPs from backend...`);
  
  // Normalize ALL input rsids
  const normalizedInput = rsids.map(normalizeRsid).filter(Boolean);
  
  // Get session-specific cache
  const sessionData = getSessionCache(sessionId);
  const cache = sessionData.cache;
  
  // Check cache validity
  isCacheValid(sessionId);
  
  // Filter out cached SNPs (using normalized keys)
  const uncachedRsids = normalizedInput.filter(rsid => !cache.has(rsid));
  const cachedCount = normalizedInput.length - uncachedRsids.length;
  
  if (cachedCount > 0) {
    console.log(`✅ [${sessionId}] Found ${cachedCount.toLocaleString()} SNPs in cache`);
  }
  
  // Build database from cache first
  const database = {};
  normalizedInput.forEach(rsid => {
    if (cache.has(rsid)) {
      database[rsid] = cache.get(rsid);
    }
  });
  
  if (uncachedRsids.length === 0) {
    console.log(`✅ [${sessionId}] All SNPs loaded from cache`);
    return { database, sessionId }; // ✅ Return object with sessionId
  }
  
  console.log(`🔍 [${sessionId}] Need to fetch ${uncachedRsids.length.toLocaleString()} uncached SNPs`);
  
  // Chunk size: 5,000 rsIDs per request for faster response
  const CHUNK_SIZE = 5000;
  
  try {
    const chunks = [];
    for (let i = 0; i < uncachedRsids.length; i += CHUNK_SIZE) {
      chunks.push(uncachedRsids.slice(i, i + CHUNK_SIZE));
    }
    
    console.log(`📦 [${sessionId}] Split into ${chunks.length} chunks of up to ${CHUNK_SIZE.toLocaleString()} SNPs each`);
    
    // Process chunks in parallel (max 3 at a time to avoid overwhelming server)
    const MAX_PARALLEL = 1;
    let processedChunks = 0;
    let totalMatches = 0;
    
    for (let i = 0; i < chunks.length; i += MAX_PARALLEL) {
      const parallelChunks = chunks.slice(i, i + MAX_PARALLEL);
      
      const promises = parallelChunks.map(async (chunk, idx) => {
        const chunkIndex = i + idx;
        console.log(`⏳ [${sessionId}] Fetching chunk ${chunkIndex + 1}/${chunks.length} (${chunk.length.toLocaleString()} SNPs)...`);
        
        const startTime = Date.now();
        
        try {
          const response = await fetch(`${API_URL}/api/clinvar/batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ rsids: chunk })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ [${sessionId}] Chunk ${chunkIndex + 1} failed (${response.status}):`, errorText);
            return { success: false, data: [], chunkIndex };
          }
          
          const data = await response.json();
          const elapsed = Date.now() - startTime;
          console.log(`✅ [${sessionId}] Chunk ${chunkIndex + 1}: Found ${data.length.toLocaleString()} matches in ${elapsed}ms`);
          
          return { success: true, data, chunkIndex };
        } catch (err) {
          console.error(`❌ [${sessionId}] Error fetching chunk ${chunkIndex + 1}:`, err.message);
          return { success: false, data: [], chunkIndex, error: err.message };
        }
      });
      
      const results = await Promise.all(promises);
      
      // Process results from all parallel chunks
      results.forEach(({ success, data, chunkIndex, error }) => {
        if (!success) {
          console.warn(`⚠️ [${sessionId}] Chunk ${chunkIndex + 1} failed, continuing with partial results`);
          return;
        }
        
        data.forEach(row => {
          const normalizedRsid = normalizeRsid(row.rsid);
          if (!normalizedRsid) return;
          
          const entry = {
            chromosome: row.chromosome,
            position: row.position?.toString(),
            gene: row.gene || row.rsid,
            variants: {
              general: {
                magnitude: row.magnitude,
                summary: row.summary,
                repute: row.repute,
                description: row.description,
                clinicalSignificance: row.clinical_significance,
                reviewStatus: row.review_status,
                conditions: row.conditions,
                url: `https://www.ncbi.nlm.nih.gov/clinvar/?term=${row.rsid}`
              }
            }
          };
          
          // Only add if not already in database (prevent overwrites from race conditions)
          if (!database[normalizedRsid]) {
            database[normalizedRsid] = entry;
            cache.set(normalizedRsid, entry); // Cache with normalized key
            totalMatches++;
          }
        });
      });
      
      processedChunks += parallelChunks.length;
      
      // Report progress after each parallel batch
      if (onProgress) {
        const totalFetched = Object.keys(database).length;
        const progress = Math.round((processedChunks / chunks.length) * 100);
        onProgress(progress, totalFetched);
      }
    }
    
    console.log(`🎉 [${sessionId}] Batch complete: ${Object.keys(database).length.toLocaleString()} total variants (${cachedCount.toLocaleString()} from cache, ${totalMatches.toLocaleString()} newly fetched)`);
    return { database, sessionId }; // ✅ Return object with sessionId
    
  } catch (err) {
    console.error(`❌ [${sessionId}] Critical error fetching ClinVar data:`, err);
    
    // Return whatever we have (cached + partial results)
    console.log(`⚠️ [${sessionId}] Returning partial results: ${Object.keys(database).length.toLocaleString()} variants`);
    return { database, sessionId }; // ✅ Return object with sessionId
  }
};

/**
 * Fetch single SNP
 */
export const fetchClinVarSingle = async (rsid, sessionId = null) => {
  if (!sessionId) {
    sessionId = generateSessionId();
  }
  
  const normalizedRsid = normalizeRsid(rsid);
  if (!normalizedRsid) {
    console.error('Invalid rsID:', rsid);
    return { data: null, sessionId };
  }
  
  // Get session-specific cache
  const sessionData = getSessionCache(sessionId);
  const cache = sessionData.cache;
  
  // Check cache first
  isCacheValid(sessionId);
  
  if (cache.has(normalizedRsid)) {
    console.log(`✅ [${sessionId}] Found ${normalizedRsid} in cache`);
    return { data: { [normalizedRsid]: cache.get(normalizedRsid) }, sessionId };
  }
  
  try {
    const response = await fetch(`${API_URL}/api/clinvar/${normalizedRsid}`);
    
    if (response.status === 404) {
      console.log(`ℹ️ [${sessionId}] SNP ${normalizedRsid} not found in database`);
      return { data: null, sessionId };
    }
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const row = await response.json();
    
    const entry = {
      chromosome: row.chromosome,
      position: row.position?.toString(),
      gene: row.gene || row.rsid,
      variants: {
        general: {
          magnitude: row.magnitude,
          summary: row.summary,
          repute: row.repute,
          description: row.description,
          clinicalSignificance: row.clinical_significance,
          reviewStatus: row.review_status,
          conditions: row.conditions,
          url: `https://www.ncbi.nlm.nih.gov/clinvar/?term=${row.rsid}`
        }
      }
    };
    
    cache.set(normalizedRsid, entry);
    
    return { data: { [normalizedRsid]: entry }, sessionId };
  } catch (err) {
    console.error(`❌ [${sessionId}] Error fetching SNP:`, err);
    return { data: null, sessionId };
  }
};

/**
 * Get cache statistics (useful for debugging)
 */
export const getCacheStats = (sessionId) => {
  if (!sessionId || !sessionCaches.has(sessionId)) {
    return {
      sessionId,
      exists: false,
      activeSessions: sessionCaches.size
    };
  }
  
  const sessionData = sessionCaches.get(sessionId);
  const now = Date.now();
  const age = Math.round((now - sessionData.timestamp) / 1000);
  
  return {
    sessionId,
    exists: true,
    size: sessionData.cache.size,
    ageSeconds: age,
    valid: isCacheValid(sessionId),
    expiresIn: Math.max(0, Math.round((CACHE_DURATION - (now - sessionData.timestamp)) / 1000)),
    activeSessions: sessionCaches.size
  };
};

/**
 * Fallback data for offline use
 */
export const loadClinVarFallback = () => {
  console.log('Using ClinVar fallback data (offline mode)');
  
  return {
    'rs334': {
      chromosome: '11',
      position: '5227002',
      gene: 'HBB',
      variants: {
        general: {
          magnitude: 5,
          summary: 'Pathogenic variant',
          repute: 'bad',
          description: 'Clinical significance: Pathogenic. Located in the HBB gene. Associated with: Sickle cell anemia.',
          clinicalSignificance: 'Pathogenic',
          reviewStatus: 'reviewed by expert panel',
          conditions: 'Sickle cell anemia',
          url: 'https://www.ncbi.nlm.nih.gov/clinvar/?term=rs334'
        }
      }
    }
  };
};

export default loadClinVarData;
